import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getTaskItemsQueryKey } from "~/pages/dashboard/task/hooks/apis/use-task-items";
import type { Item } from "~/types/api";

type Props = {
  taskId: number;
  allItemsSelected: boolean;
  selectedItemIds: number[];
};

export const useTasksInfo = (items: Item[]): Props[] => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    if (items.length === 0) return [];

    //Grouping items by taskId
    const itemsByTask = items.reduce(
      (acc, item) => {
        if (!acc[item.taskId]) {
          acc[item.taskId] = [];
        }
        acc[item.taskId]!.push(item);
        return acc;
      },
      {} as Record<number, Item[]>
    );

    //Checking if for each task all items are selected
    const taskUpdates = Object.entries(itemsByTask)
      .map(([taskIdStr, taskItems]) => {
        const taskId = Number(taskIdStr);
        const taskItemsQueryKey = getTaskItemsQueryKey(taskId);
        const allTaskItems = queryClient.getQueryData<Item[]>(taskItemsQueryKey);

        if (!allTaskItems) return null;

        const allItemsSelected = allTaskItems.length === taskItems.length;

        return {
          taskId,
          allItemsSelected,
          selectedItemIds: taskItems.map((item) => item.id),
        };
      })
      .filter((task): task is NonNullable<typeof task> => task !== null);

    return taskUpdates;
  }, [items]);
};
